/**
 * Perth Property Pulse — Reading Room article store.
 * Single source of truth for both the listing (on the main page) and the
 * individual article pages at /perth-property-pulse/articles/:slug.
 *
 * CONTENT INTEGRITY: every figure in a published note is traceable to a named
 * source with a reference period. Unpublished notes are clearly marked
 * (published: false) and render as "Source review required".
 */
const u = (id, w = 1600) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;
const PROD = 'https://static.prod-images.emergentagent.com/jobs/8eb1e5b0-8e8c-4e88-832b-2916713b4c7a/images/';
const PX = (id) => `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600`;

export const ARTICLES = [
  {
    slug: 'perth-homeowners-month-ahead',
    category: 'The Pulse',
    title: "The Perth Homeowner's Month Ahead",
    excerpt:
      'A calm, sourced look at where Perth property sits right now — and the handful of signals worth watching this month.',
    readTime: '6 min read',
    image: PX(35174505),
    published: true,
    updated: 'June 2026',
    dek: 'Beneath the headlines, prices, rents, listings and demand shift quietly. The calmest way to read the month ahead is to start with what the numbers actually say — and where they came from.',
    body: [
      { type: 'p', text: "Perth property rarely moves in a straight line. The calmest way to read the month ahead is to begin with what the numbers actually say, and to be honest about where they came from and how fresh they are." },
      { type: 'h2', text: 'Where the market sits today' },
      { type: 'p', text: 'Over the twelve months to May 2026, REIWA put Perth’s median house sale price at $920,000 and the median unit price at $660,000, with land at a median of $415,000. These are rolling annual medians drawn from Landgate and REIWA settlement data, and they are revised as more sales settle.' },
      { type: 'p', text: 'Zoom out, and the Australian Bureau of Statistics tells a similar story of strength. In its December quarter 2025 release, the ABS estimated the mean value of a Western Australian dwelling at $1,014,200 — and noted WA recorded one of the strongest quarterly rises in the country, up 7.8 per cent for the quarter.' },
      { type: 'callout', title: 'How to read these numbers', text: 'Medians and means describe the middle of a large, varied market. They do not describe your street, your block or your home. Treat them as weather, not a forecast — and always check the reference period and the source.' },
      { type: 'h2', text: 'Rents, and a market still tight' },
      { type: 'p', text: 'On the rental side, REIWA’s data to May 2026 showed a median house rent of about $750 per week and a median unit rent of around $700 per week. In a single recent week — ending 31 May 2026 — REIWA counted roughly 1,617 houses and 703 units available to rent across the metro, a useful reminder of how thin choice can still feel for renters.' },
      { type: 'h2', text: 'What’s actually changing hands' },
      { type: 'p', text: 'Activity is its own signal. In the week ending 31 May 2026, REIWA recorded around 680 sales across houses, units and land, against roughly 5,330 properties listed for sale. When listings rise faster than sales, buyers slowly gain a little breathing room; when they do not, competition stays firm.' },
      { type: 'h2', text: 'Three things worth watching this month' },
      { type: 'list', items: [
        'Listings versus sales — are more homes coming to market than are selling? That balance shapes how much room buyers have.',
        'Interest-rate decisions — borrowing costs quietly move what buyers can offer, and how confident sellers feel.',
        'The seasonal rhythm — Perth has a natural winter quiet and a spring lift; timing often matters as much as price.',
      ] },
      { type: 'p', text: 'None of these are predictions. They are simply the dials worth glancing at before you make a move — the same ones we will keep translating, in plain English, as the Pulse grows.' },
    ],
    sources: [
      { name: 'REIWA — Perth metro market data', url: 'https://reiwa.com.au/the-wa-market/perth-metro/', detail: 'Median prices and rents (12 months to May 2026); weekly sales, listings and rental availability (week ending 31 May 2026). Source: Landgate/REIWA, last updated 4 June 2026.' },
      { name: 'Australian Bureau of Statistics — Total Value of Dwellings', url: 'https://www.abs.gov.au/statistics/economy/price-indexes-and-inflation/total-value-dwellings/latest-release', detail: 'WA mean dwelling price $1,014,200 and +7.8% quarterly growth, December quarter 2025 (released 10 March 2026).' },
    ],
  },
  {
    slug: 'planning-fine-print-change-a-street',
    category: 'Planning',
    title: 'The Planning Fine Print That Can Change a Street',
    excerpt: 'Why zoning, precincts and density quietly matter more than most of us realise.',
    readTime: '6 min read',
    image: PX(12281850),
    published: false,
  },
  {
    slug: 'keep-a-perth-home-cooler',
    category: 'Home Notes',
    title: 'How to Keep a Perth Home Cooler Without Losing Its Beauty',
    excerpt: 'Low-cost ways to manage heat, shade and light through the hottest months.',
    readTime: '4 min read',
    image: u('photo-1600585154340-be6161a56a0c'),
    published: false,
  },
  {
    slug: 'waterwise-garden-still-feels-lush',
    category: 'The Garden',
    title: 'The Waterwise Garden That Still Feels Lush',
    excerpt: 'How to design a garden that survives a Perth summer and still feels considered.',
    readTime: '5 min read',
    image: `${PROD}2967f44df00267da41feae0a4ce231da3e496d5e41ba8ca5b84e09e1e498ee27.png`,
    published: false,
  },
  {
    slug: 'what-buyers-notice-after-the-open-home',
    category: "Buyer's Eye",
    title: 'What Buyers Notice After the Open Home',
    excerpt: 'Orientation, soil, planning and light — the questions a photo never answers.',
    readTime: '7 min read',
    image: u('photo-1568605114967-8130f3a36994'),
    published: false,
  },
  {
    slug: 'quiet-signals-of-a-changing-suburb',
    category: 'Suburbs',
    title: 'The Quiet Signals of a Changing Suburb',
    excerpt: 'Transport, services and amenity often move long before prices do.',
    readTime: '6 min read',
    image: u('photo-1449157291145-7efd050a4d0e'),
    published: false,
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
